// 📄 pages/views/configuracionCAFO.tsx
// Shell de "Configuración": agrupa, por pestaña, la configuración de
// conexión/credenciales de cada app soportada para el cliente del
// proyecto actual (ver src/components/Configuracion/*). "Plantillas XML"
// es la excepción: es configuración a nivel PROYECTO, no cliente (ver
// ConfiguracionPlantillasXml.tsx).
import { useState } from "react";
import { Settings } from "lucide-react";
import { useProject } from "../../context/ProjectContext.tsx";
import ConfiguracionCig from "../../components/Configuracion/ConfiguracionCig.tsx";
import ConfiguracionSunat from "../../components/Configuracion/ConfiguracionSunat.tsx";
import ConfiguracionApis from "../../components/Configuracion/ConfiguracionApis.tsx";
import ConfiguracionPlantillasXml from "../../components/Configuracion/ConfiguracionPlantillasXml.tsx";
import ConfiguracionTransaccionesZ from "../../components/Configuracion/ConfiguracionTransaccionesZ.tsx";
import ConfiguracionBadisZ from "../../components/Configuracion/ConfiguracionBadisZ.tsx";
import ConfiguracionInterfacesZ from "../../components/Configuracion/ConfiguracionInterfacesZ.tsx";
import ConfiguracionProgramasZ from "../../components/Configuracion/ConfiguracionProgramasZ.tsx";
import ConfiguracionTablasZ from "../../components/Configuracion/ConfiguracionTablasZ.tsx";

type Tab = "cig" | "sunat" | "apis" | "plantillasXml" | "transaccionesZ";

const TABS: { value: Tab; label: string }[] = [
  { value: "cig", label: "CIG" },
  { value: "sunat", label: "SUNAT" },
  { value: "apis", label: "APIs" },
  { value: "plantillasXml", label: "Plantillas XML" },
  { value: "transaccionesZ", label: "Desarrollo Z" }
];

export default function Configuracioncafo() {
  const { project, projectId } = useProject();
  const clienteId = project?.clienteId || "";
  const [tab, setTab] = useState<Tab>("cig");

  if (!project) return null;

  return (
    <div className="space-y-6 max-w-2xl p-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-blue-500/20 rounded-2xl text-blue-400"><Settings size={24} /></div>
        <div>
          <h2 className="text-xl font-bold text-white">Configuración</h2>
          <p className="text-sm text-gray-400">Credenciales y datos de conexión guardados para este cliente.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
              tab === t.value
                ? "border-blue-500 text-white"
                : "border-transparent text-gray-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "cig" && <ConfiguracionCig clienteId={clienteId} />}
      {tab === "sunat" && <ConfiguracionSunat clienteId={clienteId} />}
      {tab === "apis" && <ConfiguracionApis clienteId={clienteId} />}
      {tab === "plantillasXml" && <ConfiguracionPlantillasXml projectId={projectId} projectNombre={project.nombre} />}

      {tab === "transaccionesZ" && (
        <div className="space-y-8">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-bold text-white">Transacciones Z</h3>
              <p className="text-xs text-gray-500 mt-0.5">Códigos de transacción SAP personalizados, desarrollo propio de este proyecto.</p>
            </div>
            <ConfiguracionTransaccionesZ projectId={projectId} projectNombre={project.nombre} />
          </div>

          <div className="pt-6 border-t border-white/10 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white">BAdIs Z</h3>
              <p className="text-xs text-gray-500 mt-0.5">BAdIs y objetos ABAP personalizados, desarrollo propio de este proyecto.</p>
            </div>
            <ConfiguracionBadisZ projectId={projectId} projectNombre={project.nombre} />
          </div>

          <div className="pt-6 border-t border-white/10 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white">Interfaces / Canales Z</h3>
              <p className="text-xs text-gray-500 mt-0.5">Interfaces y canales de integración personalizados, desarrollo propio de este proyecto.</p>
            </div>
            <ConfiguracionInterfacesZ projectId={projectId} projectNombre={project.nombre} />
          </div>

          <div className="pt-6 border-t border-white/10 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white">Programas Z</h3>
              <p className="text-xs text-gray-500 mt-0.5">Programas e includes ABAP personalizados, desarrollo propio de este proyecto, relacionables con BAdIs y Transacciones.</p>
            </div>
            <ConfiguracionProgramasZ projectId={projectId} projectNombre={project.nombre} />
          </div>

          <div className="pt-6 border-t border-white/10 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white">Tablas Z</h3>
              <p className="text-xs text-gray-500 mt-0.5">Tablas SAP personalizadas (ej. vía SE16N), con sus campos de referencia, desarrollo propio de este proyecto.</p>
            </div>
            <ConfiguracionTablasZ projectId={projectId} projectNombre={project.nombre} />
          </div>
        </div>
      )}
    </div>
  );
}
